"use client";

import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./listing-settings-modal.module.css";
import type {
  ListingBillingDetails,
  ListingChanges,
  UserListingCard,
} from "../../../types";
import Image from "next/image";
import { getPaymentIcon } from "@/lib/payment-icons/payment-icons";
import { MdClose } from "react-icons/md";
import { capitalize } from "@/lib/capitalize-text";
import { ListingTypeEnum } from "@/types/types";
import { useMyListingsStore } from "../../../useMyListingsStore";
import ActiveSubscriptionSection from "./active-subscription-section";
import NoSubscriptionSection from "./no-subscription-section";
import { sleep } from "@/lib/sleep";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

export default function ListingSettingsModal({
  listingData,
  dialogRef,
  sessionId,
  closeCallbackAction,
}: {
  listingData: UserListingCard;
  dialogRef: MutableRefObject<HTMLDivElement | null>;
  sessionId: string | null;
  closeCallbackAction: () => void;
}) {
  console.log(listingData.expires_at);
  const { saveListingSettings } = useMyListingsStore();
  const [status, setStatus] = useState(listingData.status);
  const [plan, setPlan] = useState<ListingTypeEnum>(
    listingData.listing_type?.toLowerCase() as ListingTypeEnum,
  );

  // UI & Network States
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animateIn, setAnimateIn] = useState(false);
  const [userTappedCancel, setUserTappedCancel] = useState(false);

  // Async Billing States
  const [billingDetails, setBillingDetails] =
    useState<ListingBillingDetails | null>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState(true);

  // Trigger modal open animation
  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(true), 10);
    return () => clearTimeout(t);
  }, []);

  // Sync basic listing details
  useEffect(() => {
    setPlan(listingData.listing_type?.toLowerCase() as ListingTypeEnum);
    setStatus(listingData.status);
  }, [listingData.listing_type, listingData.status]);

  // Fetch Billing Details On-Demand
  useEffect(() => {
    async function fetchBilling() {
      try {
        setIsLoadingBilling(true);
        setError(null);

        const res = await fetch(
          `/api/listings/getListingBillingInfo?listing_id=${listingData.listing_id}`,
          {
            method: "GET",
          },
        );

        if (!res.ok) throw new Error("Failed to load billing details");

        const data: ListingBillingDetails = await res.json();
        setBillingDetails(data);
        if (data.will_cancel) setUserTappedCancel(true);
      } catch (err: any) {
        setError(err.message || "Could not fetch billing details.");
      } finally {
        setIsLoadingBilling(false);
      }
    }

    if (listingData.listing_id && sessionId) {
      fetchBilling();
    }
  }, [listingData.listing_id, sessionId]);

  useDialogRouting("ListingSettingsModal", true, closeCallbackAction);

  const buildChanges = (): ListingChanges | null => {
    if (!billingDetails) return null;

    const oldStatus = listingData.status;
    const oldPlan = listingData.listing_type?.toLowerCase() as ListingTypeEnum;
    const hasPlan = billingDetails.has_active_plan;
    const willCancel = billingDetails.will_cancel;

    const changes: ListingChanges = {};

    // 1. Status Changes
    if (oldStatus !== status) {
      changes.status = status;
    }

    // 2. Plan / Billing Changes (Only if they have a plan)
    if (hasPlan) {
      if (!willCancel && !billingDetails.pending_change && plan !== oldPlan) {
        changes.newPlan = plan;
      }
      if (userTappedCancel && !willCancel) {
        changes.cancelPlan = true;
      }
    }

    if (!changes.status && !changes.newPlan && !changes.cancelPlan) {
      return null;
    }

    return changes;
  };

  async function handleSave() {
    try {
      setSaving(true);
      setError(null);

      const changes = buildChanges();
      if (!changes) {
        closeCallbackAction();
        return;
      }

      await saveListingSettings(listingData.listing_id, sessionId, changes);
      setSaving(false);
      closeCallbackAction();
    } catch (e: any) {
      setError(e?.message || "Failed to save changes. Please try again.");
      setSaving(false);
    }
  }

  console.log(billingDetails);

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        e.stopPropagation();
        closeCallbackAction();
      }}
    >
      <div
        className={`${styles.modal} ${animateIn ? styles.show : ""}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTexts}>
            <h2 className={styles.title}>Listing Settings</h2>
            <p className={styles.subtitle}>
              Manage visibility, plan, and billing for{" "}
              <span className={styles.listingName}>{listingData.title}</span>
            </p>
          </div>
          <button
            className={styles.closeBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeCallbackAction();
            }}
            aria-label="Close settings"
          >
            <MdClose />
          </button>
        </div>

        <div className={styles.divider} />

        {/* Dynamic Content */}
        {isLoadingBilling ? (
          <div className={styles.skeletonContainer}>
            {/* Overview Skeleton */}
            <div className={styles.overview}>
              <div className={`${styles.kv} ${styles.idKV}`}>
                <div
                  className={`${styles.skeletonPulse} ${styles.skelLabel}`}
                />
                <div
                  className={`${styles.skeletonPulse} ${styles.skelValue}`}
                />
              </div>
              <div className={styles.kv}>
                <div
                  className={`${styles.skeletonPulse} ${styles.skelLabel}`}
                />
                <div
                  className={`${styles.skeletonPulse} ${styles.skelValue}`}
                />
              </div>
            </div>

            {/* Grid content Skeleton */}
            <div className={styles.grid}>
              {/* Card 1 Skeleton */}
              <section className={styles.card}>
                <div
                  className={`${styles.skeletonPulse} ${styles.skelTitle}`}
                />
                <div className={`${styles.skeletonPulse} ${styles.skelText}`} />
                <div
                  className={`${styles.skeletonPulse} ${styles.skelText} ${styles.skelTextShort}`}
                />

                <div className={styles.statusRow}>
                  <div
                    className={`${styles.skeletonPulse} ${styles.skelLabel}`}
                    style={{ margin: 0, width: "30%" }}
                  />
                  <div
                    className={`${styles.skeletonPulse} ${styles.skelBadge}`}
                  />
                </div>
                <div
                  className={`${styles.skeletonPulse} ${styles.skelButton}`}
                />

                <div className={styles.innerDivider} />

                <div
                  className={`${styles.skeletonPulse} ${styles.skelTitle}`}
                  style={{ marginTop: "16px", width: "40%" }}
                />
                <div className={styles.paymentRow}>
                  <div
                    className={`${styles.skeletonPulse} ${styles.skelAvatar}`}
                  />
                  <div className={styles.skelContent}>
                    <div
                      className={`${styles.skeletonPulse} ${styles.skelText}`}
                      style={{ margin: 0, width: "60%" }}
                    />
                    <div
                      className={`${styles.skeletonPulse} ${styles.skelText}`}
                      style={{ margin: 0, width: "40%" }}
                    />
                  </div>
                </div>
              </section>

              {/* Card 2 Skeleton */}
              <section className={styles.card}>
                <div
                  className={`${styles.skeletonPulse} ${styles.skelTitle}`}
                />
                <div className={`${styles.skeletonPulse} ${styles.skelText}`} />
                <div
                  className={`${styles.skeletonPulse} ${styles.skelText} ${styles.skelTextShort}`}
                />

                <div
                  className={`${styles.skeletonPulse} ${styles.skelLabel}`}
                  style={{ marginTop: "12px" }}
                />
                <div
                  className={`${styles.skeletonPulse} ${styles.skelInput}`}
                />

                <div className={`${styles.skeletonPulse} ${styles.skelText}`} />
                <div
                  className={`${styles.skeletonPulse} ${styles.skelText} ${styles.skelTextShort}`}
                />

                <div
                  className={`${styles.skeletonPulse} ${styles.skelButton}`}
                  style={{ marginTop: "auto" }}
                />
              </section>
            </div>
          </div>
        ) : (
          <>
            {/* Overview */}
            <div className={styles.overview}>
              <div className={`${styles.kv} ${styles.idKV}`}>
                <span className={styles.k}>Listing ID</span>
                <span className={styles.v}>{listingData.listing_id}</span>
              </div>
              <div className={styles.kv}>
                <span className={styles.k}>
                  <span
                    className={`${styles.planPill} ${styles[listingData.listing_type]}`}
                  >
                    {capitalize(listingData.listing_type)}
                  </span>{" "}
                  • Next Bill
                </span>
                <span className={styles.v}>
                  {billingDetails?.next_bill_epoch
                    ? new Date(
                        billingDetails.next_bill_epoch,
                      ).toLocaleDateString()
                    : "None"}
                </span>
              </div>
            </div>

            {/* Grid content */}
            <div className={styles.grid}>
              {/* Status Section */}
              <section className={styles.card}>
                <h3 className={styles.cardTitle}>Status & Billing</h3>
                <p className={styles.cardText}>
                  Manage your listing visibility and billing details below. When
                  your listing is <b>active</b>, it’s visible on the platform.
                </p>

                {/* --- 1. LISTING VISIBILITY ONLY --- */}
                <div className={styles.statusRow}>
                  <span className={styles.statusLabel}>
                    Listing Visibility:
                  </span>
                  <span
                    className={`${styles.statusBadge} ${
                      status === "active"
                        ? styles.statusActive
                        : styles.statusInactive
                    }`}
                  >
                    {status === "active"
                      ? "Active (Public)"
                      : "Inactive (Hidden)"}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setStatus((prev) =>
                      prev === "active" ? "inactive" : "active",
                    )
                  }
                  className={`${styles.statusButton} ${
                    status === "active"
                      ? styles.deactivateBtn
                      : styles.activateBtn
                  }`}
                >
                  {status === "active"
                    ? "Deactivate Listing"
                    : "Activate Listing"}
                </button>

                <div className={styles.innerDivider} />

                {/* --- 2. BILLING INFO & SUBSCRIPTION STATUS --- */}
                <div className={styles.billingInfo}>
                  <h4 className={styles.billingTitle}>Billing Details</h4>

                  {/* Plan Status Badge */}
                  {billingDetails?.has_active_plan ||
                  billingDetails?.subscription_status === "PAST_DUE" ||
                  billingDetails?.will_cancel ? (
                    <div
                      className={`${styles.statusRow} ${styles.subscription}`}
                    >
                      <span className={styles.statusLabel}>Subscription:</span>

                      {billingDetails?.subscription_status === "PAST_DUE" ? (
                        <span
                          className={`${styles.statusBadge} ${styles.statusError}`}
                        >
                          Past Due
                        </span>
                      ) : billingDetails?.subscription_status === "CANCELED" ? (
                        <span
                          className={`${styles.statusBadge} ${styles.statusError}`}
                        >
                          Cancelled
                        </span>
                      ) : billingDetails?.will_cancel ? (
                        <span
                          className={`${styles.statusBadge} ${styles.statusCancel}`}
                        >
                          Cancels Soon
                        </span>
                      ) : billingDetails?.pending_change ? (
                        <span
                          className={`${styles.statusBadge} ${styles.statusDowngrade}`}
                        >
                          Downgrading
                        </span>
                      ) : (
                        <span
                          className={`${styles.statusBadge} ${styles.statusActive}`}
                        >
                          Active
                        </span>
                      )}
                    </div>
                  ) : null}

                  {/* Payment Method Rendering */}
                  {billingDetails?.payment_method?.type === "CREDITS" ? (
                    <div className={styles.paymentRow}>
                      <div className={styles.pmText}>
                        <div className={styles.pmLine}>
                          <span className={styles.pmBrand}>Agency Credits</span>
                        </div>
                        <div className={styles.pmMeta}>
                          Auto-renewing from agency wallet
                        </div>
                      </div>
                    </div>
                  ) : billingDetails?.payment_method?.brand ? (
                    <div className={styles.paymentRow}>
                      <div className={styles.pmIcon}>
                        <Image
                          src={getPaymentIcon(
                            billingDetails.payment_method.brand,
                          )}
                          width={48}
                          height={32}
                          alt={`${billingDetails.payment_method.brand} logo`}
                          className={styles.cardImage}
                        />
                      </div>
                      <div className={styles.pmText}>
                        <div className={styles.pmLine}>
                          <span className={styles.pmBrand}>
                            {billingDetails.payment_method.brand.toUpperCase()}
                          </span>
                          <span className={styles.pmLast4}>
                            •••• {billingDetails.payment_method.last4}
                          </span>
                        </div>
                        {billingDetails.payment_method.added_at && (
                          <div className={styles.pmMeta}>
                            {billingDetails.payment_method.type} • Added{" "}
                            {new Date(
                              billingDetails.payment_method.added_at * 1000,
                            ).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className={styles.cardTextMuted}>
                      No payment method linked to this listing.
                    </p>
                  )}

                  {/* Next Bill Date */}
                  {billingDetails?.next_bill_epoch ? (
                    <p className={styles.nextBillText}>
                      Next billing date:{" "}
                      <b>
                        {new Date(
                          billingDetails.next_bill_epoch,
                        ).toLocaleDateString()}
                      </b>
                    </p>
                  ) : null}
                </div>
              </section>

              {/* Plan Section */}
              <section className={styles.card}>
                <h3 className={styles.cardTitle}>Subscription Plan</h3>
                <p className={styles.cardText}>
                  Your plan defines how your listing is promoted.
                  {billingDetails?.has_active_plan
                    ? " Upgrade or schedule a downgrade for your next billing cycle."
                    : " Select your preferred plan to activate your listing."}
                </p>

                {billingDetails?.has_active_plan ? (
                  <ActiveSubscriptionSection
                    listingData={listingData}
                    userTappedCancel={userTappedCancel}
                    setUserTappedCancelAction={setUserTappedCancel}
                    plan={plan}
                    setPlanAction={setPlan}
                    pendingChange={billingDetails?.pending_change}
                    alreadyCanceled={billingDetails?.will_cancel}
                  />
                ) : (
                  <NoSubscriptionSection
                    listingData={listingData}
                    sessionId={sessionId}
                    closeCallbackAction={closeCallbackAction}
                  />
                )}
              </section>
            </div>
          </>
        )}

        {error && (
          <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <button
            className={styles.btnGhost}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              closeCallbackAction();
            }}
            disabled={saving || isLoadingBilling}
          >
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving || isLoadingBilling}
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
